/**
 * React component for rendering popout widgets.
 *
 * This component listens to the popout widgets store and opens a new browser window
 * for each widget added. It uses React portals to render the widget's React component
 * into the container of the new window. When a widget is removed from the store or
 * the popout window is closed, the widget is cleaned up accordingly.
 *
 * Dependencies:
 * - componentMap: Maps widget IDs to React components.
 * - usePopoutWidgetsStore: Zustand store for managing popout widgets.
 * - openPopoutWindow: Utility to open a new browser window and provide a container for rendering.
 */
import { componentMap } from "@/utils/componentMap";
import { ReactElement, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { popoutKey, usePopoutWidgetsStore } from "@/stores/PopoutWidgetsStore";
import { openPopoutWindow } from "@/utils/popout";
import { WidgetContainer } from "@/layout-components/WidgetContainer";
import { PortalProvider } from "@/shared/context/PortalContext/PortalContext";
import { DensityProvider } from "@/contexts/DensityContext";

function PopoutWidgets() {
  const popoutWidgets = usePopoutWidgetsStore((state) => state.widgets);

  const removePopoutWidget = usePopoutWidgetsStore(
    (state) => state.removeWidget
  );

  const [widgetPortals, setWidgetPortals] = useState<
    [ReactElement, HTMLDivElement][]
  >([]);

  // Keep track of which widgets have already been processed
  const processedWidgets = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (popoutWidgets.length === 0) {
      // Clear all when no widgets
      setWidgetPortals([]);
      processedWidgets.current.clear();
      return;
    }

    // Only process new widgets that haven't been processed yet. Tracked by
    // instance key, not widget type, so a second copy of the same widget gets
    // its own window rather than being skipped as a duplicate.
    const newWidgets = popoutWidgets.filter(
      (widget) => !processedWidgets.current.has(popoutKey(widget))
    );

    newWidgets.forEach((widget) => {
      const key = popoutKey(widget);
      const WidgetComponent =
        componentMap[widget.id as keyof typeof componentMap];

      if (!WidgetComponent) {
        console.error(`No component found for widget ID: ${widget.id}`);
        return;
      }

      openPopoutWindow({
        title: widget.name,
        // window.open() reuses any window already opened under the same name, so
        // the name has to be per-instance or the second popout would replace the
        // first one's contents. The visible title stays the plain widget name.
        windowName: `newsquawk-popout-${key}`,

        onContainerReady: (container, window) => {
          const SearchComponent =
            componentMap[widget.component as keyof typeof componentMap]
              .SearchComponent || null;

          setWidgetPortals((prev) => [
            ...prev,
            [
              <PortalProvider container={window.document.body} key={key}>
                <DensityProvider>
                <WidgetContainer
                  widget={widget}
                  widgetCompositeId={widget.widgetCompositeId || widget.id}
                  onClose={() => removePopoutWidget(key)}
                  searchComponent={SearchComponent}
                >
                  {(headerOptions) => (
                    <WidgetComponent
                      headerOptions={headerOptions}
                      widgetCompositeId={widget.widgetCompositeId || widget.id}
                    />
                  )}
                </WidgetContainer>
                </DensityProvider>
              </PortalProvider>,
              container,
            ],
          ]);

          // Mark this instance as processed
          processedWidgets.current.add(key);

          window.addEventListener("beforeunload", () => {
            removePopoutWidget(key);
            processedWidgets.current.delete(key);
          });
        },
      });
    });

    const processed = processedWidgets.current;

    // Cleanup function
    return () => {
      // Remove widgets that are no longer in the store
      const currentKeys = new Set(popoutWidgets.map(popoutKey));

      processed.forEach((key) => {
        if (!currentKeys.has(key)) {
          processed.delete(key);
        }
      });
    };
  }, [popoutWidgets, removePopoutWidget]);

  return (
    <>
      {widgetPortals.map((widgetPortal, index) =>
        ReactDOM.createPortal(
          widgetPortal[0],
          widgetPortal[1],
          `portal-${index}`
        )
      )}
    </>
  );
}

export default PopoutWidgets;
